import { z } from 'zod';
/**
 * Outside-in signals (task 46): what can be observed about a business from the internet without its help.
 * Where the signals come from (a ratings vendor, our own checks, a scan dataset) is decision 0026's open
 * question (task 45); everything here is the same whichever source is chosen.
 *
 * Signals never change the CyberScore by themselves. They give an outside-in view (factor grades), and
 * say how the controls that can be seen from outside stand, which staff can accept as answers with
 * `SECURITY_SCAN` or `EXTERNAL_PROVIDER` provenance.
 */
export const SIGNAL_KINDS = ['open_port', 'software', 'tls', 'email_auth', 'http_headers', 'leaked_credentials']; // prettier-ignore
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const Hostname = z.string().trim().toLowerCase().regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'a domain name such as example.com.my'); // prettier-ignore
export const OutsideInDomain = Hostname;
const Asset = z.strictObject({
    type: z.enum(['domain', 'hostname', 'ip']),
    value: z.string().min(1).max(253),
});
const base = { asset: Asset, observed_at: IsoDate };
export const Signal = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('open_port'), ...base, port: z.number().int().min(1).max(65535), service: z.string().max(50).default('') }), // prettier-ignore
    z.strictObject({
        kind: z.literal('software'),
        ...base,
        product: z.string().min(1).max(100),
        version: z.string().max(50).default(''),
        end_of_life: z.boolean().default(false),
        /** CVEs on a known-exploited list (e.g. CISA KEV) that this version is affected by. */
        known_exploited: z.array(z.string().regex(/^CVE-\d{4}-\d{4,}$/)).default([]),
    }),
    z.strictObject({ kind: z.literal('tls'), ...base, protocol: z.enum(['TLSv1.0', 'TLSv1.1', 'TLSv1.2', 'TLSv1.3']), valid_to: IsoDate, trusted: z.boolean() }), // prettier-ignore
    z.strictObject({
        kind: z.literal('email_auth'),
        ...base,
        spf: z.enum(['pass', 'softfail', 'neutral', 'missing']),
        /** Null when unknown: DKIM keys can't be listed, so passive checks only find them under common names. */
        dkim: z.boolean().nullable(),
        dmarc: z.enum(['reject', 'quarantine', 'none', 'missing']),
    }),
    z.strictObject({ kind: z.literal('http_headers'), ...base, hsts: z.boolean(), csp: z.boolean(), frame_options: z.boolean() }), // prettier-ignore
    z.strictObject({ kind: z.literal('leaked_credentials'), ...base, accounts: z.number().int().min(0), latest_breach: IsoDate.nullable() }), // prettier-ignore
]);
/** What a source returns: the signals, and which kinds it checked (absence only means something for those). */
export const Collection = z.strictObject({
    checked: z.array(z.enum(SIGNAL_KINDS)),
    signals: z.array(Signal).max(5000),
});
/** The provenance an accepted answer records: a vendor's rating is an external provider; anything observed is a scan. */
export const SOURCE_PROVENANCE = {
    vendor: 'EXTERNAL_PROVIDER',
    scan: 'SECURITY_SCAN',
    dataset: 'SECURITY_SCAN',
    fixture: 'SECURITY_SCAN',
};
/** Ports that should never face the internet: remote desktop, file sharing, databases, remote consoles. */
export const RISKY_PORTS = {
    21: 'FTP', 23: 'Telnet', 445: 'SMB', 1433: 'SQL Server', 3306: 'MySQL', 3389: 'Remote desktop',
    5432: 'PostgreSQL', 5900: 'VNC', 6379: 'Redis', 9200: 'Elasticsearch', 27017: 'MongoDB',
}; // prettier-ignore
export const gradeOf = (n) => n >= 90 ? 'A' : n >= 80 ? 'B' : n >= 70 ? 'C' : n >= 60 ? 'D' : 'F';
const where = (s) => s.asset.value;
const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));
/** The outside-in view of a collection, as on a date (certificates expire as the days pass). */
export function assessOutsideIn(c, as_of) {
    const checked = new Set(c.checked);
    const of = (k) => c.signals.filter((s) => s.kind === k); // prettier-ignore
    const factors = [];
    const controls = [];
    const factor = (key, name, score, issues) => {
        if (!checked.has(KIND_OF[key]))
            return factors.push({ key, name, score: null, grade: null, issues: [] });
        const v = clamp(score);
        factors.push({ key, name, score: v, grade: gradeOf(v), issues });
    };
    // Network: risky services reachable from the internet.
    const risky = of('open_port').filter((p) => RISKY_PORTS[p.port]);
    factor('network', 'Exposed services', 100 - 30 * risky.length, risky.map((p) => `${RISKY_PORTS[p.port]} (port ${p.port}) open on ${where(p)}`)); // prettier-ignore
    if (checked.has('open_port')) {
        const rdp = risky.filter((p) => p.port === 3389 || p.port === 445);
        controls.push(rdp.length ? { control: 'NS-01', implementation: 0, reason: rdp.map((p) => `${RISKY_PORTS[p.port]} open on ${where(p)}`).join('; ') } : { control: 'NS-01', implementation: 1, reason: 'No remote desktop or SMB seen from the internet' }); // prettier-ignore
    }
    // Patching: end-of-life software and known exploited vulnerabilities.
    const software = of('software');
    const eol = software.filter((s) => s.end_of_life);
    const kev = software.filter((s) => s.known_exploited.length);
    factor('patching', 'Internet-facing software', 100 - 25 * eol.length - 40 * kev.length, [
        ...kev.map((s) => `${s.product} ${s.version} on ${where(s)} has known exploited vulnerabilities (${s.known_exploited.join(', ')})`), // prettier-ignore
        ...eol.map((s) => `${s.product} ${s.version} on ${where(s)} is no longer supported`),
    ]);
    if (checked.has('software')) {
        controls.push(kev.length ? { control: 'VM-02', implementation: 0, reason: kev.map((s) => `${s.product} on ${where(s)}: ${s.known_exploited.join(', ')}`).join('; ') } : { control: 'VM-02', implementation: 1, reason: 'No known exploited vulnerabilities seen' }); // prettier-ignore
        controls.push(eol.length ? { control: 'AM-03', implementation: 0, reason: eol.map((s) => `${s.product} ${s.version} on ${where(s)}`).join('; ') } : { control: 'AM-03', implementation: 1, reason: 'No unsupported software seen' }); // prettier-ignore
    }
    // TLS: expired or untrusted certificates, old protocols.
    const tls = of('tls');
    const broken = tls.filter((t) => !t.trusted || t.valid_to < as_of);
    const old = tls.filter((t) => !broken.includes(t) && (t.protocol === 'TLSv1.0' || t.protocol === 'TLSv1.1'));
    factor('tls', 'Certificates and encryption', 100 - 35 * broken.length - 15 * old.length, [
        ...broken.map((t) => `${where(t)}: certificate ${t.trusted ? `expired on ${t.valid_to}` : 'not trusted'}`),
        ...old.map((t) => `${where(t)} still accepts ${t.protocol}`),
    ]);
    if (checked.has('tls') && tls.length)
        controls.push({ control: 'NS-07', implementation: broken.length ? 0 : old.length ? 0.5 : 1, reason: broken.length || old.length ? [...broken.map((t) => `${where(t)}: certificate problem`), ...old.map((t) => `${where(t)}: ${t.protocol}`)].join('; ') : 'Valid certificates and TLS 1.2 or later' }); // prettier-ignore
    // Email: SPF, DKIM and DMARC.
    const mail = of('email_auth');
    const dmarcPts = { reject: 50, quarantine: 45, none: 15, missing: 0 };
    const spfPts = { pass: 30, softfail: 20, neutral: 10, missing: 0 };
    // DKIM not found under the common names may still be there: half its points, and said as unknown.
    const mailScore = mail.length ? Math.min(...mail.map((m) => dmarcPts[m.dmarc] + spfPts[m.spf] + (m.dkim ? 20 : m.dkim === null ? 10 : 0))) : 0; // prettier-ignore
    factor('email', 'Email protection', mailScore, mail.flatMap((m) => [
        ...(m.dmarc === 'none' || m.dmarc === 'missing' ? [`${where(m)}: DMARC ${m.dmarc === 'none' ? 'only monitors (p=none)' : 'missing'}`] : []),
        ...(m.spf !== 'pass' ? [`${where(m)}: SPF ${m.spf}`] : []),
        ...(m.dkim ? [] : m.dkim === null ? [`${where(m)}: DKIM not found under the usual names (it may use others)`] : [`${where(m)}: no DKIM`]),
    ])); // prettier-ignore
    if (checked.has('email_auth') && mail.length) {
        const worst = (xs, order) => xs.reduce((a, b) => (order.indexOf(b) > order.indexOf(a) ? b : a));
        const dmarc = worst(mail.map((m) => m.dmarc), ['reject', 'quarantine', 'none', 'missing']);
        controls.push({ control: 'EH-01', implementation: dmarc === 'reject' || dmarc === 'quarantine' ? 1 : dmarc === 'none' ? 0.5 : 0, reason: `DMARC ${dmarc === 'missing' ? 'not published' : `p=${dmarc}`}` }); // prettier-ignore
        // SPF and DKIM together; with DKIM unknown there's no reading, so nothing is contradicted on a guess.
        if (mail.every((m) => m.dkim !== null)) {
            const aligned = mail.filter((m) => m.spf === 'pass' && m.dkim).length;
            const partial = mail.filter((m) => m.spf === 'pass' || m.dkim).length;
            controls.push({ control: 'EH-06', implementation: aligned === mail.length ? 1 : partial === mail.length ? 0.5 : 0, reason: mail.map((m) => `${where(m)}: SPF ${m.spf}, DKIM ${m.dkim ? 'published' : 'missing'}`).join('; ') }); // prettier-ignore
        }
    }
    // Web: security headers.
    const web = of('http_headers');
    const missing = web.flatMap((h) => [
        ...(h.hsts ? [] : [`${where(h)}: no HSTS`]),
        ...(h.csp ? [] : [`${where(h)}: no Content-Security-Policy`]),
        ...(h.frame_options ? [] : [`${where(h)}: no clickjacking protection`]),
    ]);
    factor('web', 'Website security headers', web.length ? 100 - (missing.length / (web.length * 3)) * 60 : 100, missing); // prettier-ignore
    // Leaks: accounts in known breaches, recent ones counting most.
    const leaks = of('leaked_credentials');
    const accounts = leaks.reduce((n, l) => n + l.accounts, 0);
    const recent = leaks.some((l) => l.latest_breach !== null && l.latest_breach >= shiftYears(as_of, -1));
    factor('leaks', 'Leaked credentials', 100 - Math.min(60, accounts * 4) - (recent ? 20 : 0), accounts ? [`${accounts} account${accounts === 1 ? '' : 's'} in known breaches${recent ? ', one in the last year' : ''}`] : []); // prettier-ignore
    const scored = factors.filter((f) => f.score !== null);
    const score = scored.length ? clamp(scored.reduce((n, f) => n + f.score, 0) / scored.length) : null;
    return { as_of, score, grade: score === null ? null : gradeOf(score), factors, controls };
}
const KIND_OF = {
    network: 'open_port',
    patching: 'software',
    tls: 'tls',
    email: 'email_auth',
    web: 'http_headers',
    leaks: 'leaked_credentials',
};
function shiftYears(iso, years) {
    return `${Number(iso.slice(0, 4)) + years}${iso.slice(4)}`;
}
/**
 * The answer a control reading gives to a question: the option whose `score_change` effect credits that
 * control with that implementation. Null when the question has no such option.
 */
export function optionForReading(q, r) {
    for (const o of q.options)
        if (o.effects.some((e) => e.type === 'score_change' &&
            e.params.control === r.control &&
            e.params.implementation === r.implementation))
            // prettier-ignore
            return { value: o.value, label: o.label };
    return null;
}
// ---------- passive checks and consent (task 46, decision 0052) ----------
/** What passive checks look at: public DNS and email records, and the website as any visitor sees it. */
export const PASSIVE_KINDS = ['email_auth', 'tls', 'http_headers']; // prettier-ignore
export const ConsentDomains = z
    .array(OutsideInDomain)
    .min(1)
    .max(10)
    .transform((d) => [...new Set(d)]);
export const GiveOutsideInConsent = z.strictObject({ domains: ConsentDomains, agree: z.literal(true) });
const listOf = (xs) => xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}` : (xs[0] ?? '');
/** The sentence the business owner agrees to, kept with the consent. */
export function outsideInConsentText(p) {
    return `I agree that ${p.insurer} may check ${listOf(p.domains)} from the internet for ${p.business}: public DNS and email records, and the website’s certificate and security headers, as any visitor sees them. Nothing is scanned or tried. I can withdraw this at any time.`; // prettier-ignore
}
/** A domain is covered by a consent for it or for a domain it sits under (shop.example.my under example.my). */
export const coveredBy = (domain, consented) => consented.some((c) => domain === c || domain.endsWith(`.${c}`));
/** `.test` domains are made up (fixture data); they need no consent and never reach the internet. */
export const isMadeUp = (domain) => domain.endsWith('.test');
/**
 * How strict a domain's SPF record is, from its TXT records: `-all` is pass (strict), `~all` softfail,
 * anything weaker neutral; none, or more than one (invalid), is missing.
 */
export function readSpf(txts) {
    const records = txts.map((t) => t.trim()).filter((t) => /^v=spf1(\s|$)/i.test(t));
    if (records.length !== 1)
        return 'missing';
    const all = /(?:^|\s)([+\-~?]?)all(?:\s|$)/i.exec(records[0]);
    if (!all)
        return 'neutral';
    return all[1] === '-' ? 'pass' : all[1] === '~' ? 'softfail' : 'neutral';
}
/** A domain's DMARC policy, from the TXT records at `_dmarc.<domain>`; none, or more than one, is missing. */
export function readDmarc(txts) {
    const records = txts.map((t) => t.trim()).filter((t) => /^v=DMARC1\s*(;|$)/i.test(t));
    if (records.length !== 1)
        return 'missing';
    const p = /(?:^|;)\s*p\s*=\s*([a-z]+)\s*(?:;|$)/i.exec(records[0])?.[1]?.toLowerCase();
    return p === 'reject' || p === 'quarantine' ? p : 'none';
}
/** Whether a TXT record at `<selector>._domainkey.<domain>` publishes a DKIM key. */
export const isDkimKey = (txt) => /(?:^|;)\s*p\s*=\s*[A-Za-z0-9+/]/.test(txt);
//# sourceMappingURL=outside-in.js.map